import React, { useEffect, useRef, useState } from "react";

const GooglePlacesAutocomplete = ({
  value,
  onChange,
  onPlaceSelect,
  placeholder = "Enter address...",
  className = "",
  disabled = false,
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const onPlaceSelectRef = useRef(onPlaceSelect);
  const [isLoaded, setIsLoaded] = useState(false);

  // Keep the latest callback available to the listener below without
  // making the setup effect re-run every render (onPlaceSelect is usually
  // passed as a fresh inline arrow function by the caller).
  useEffect(() => {
    onPlaceSelectRef.current = onPlaceSelect;
  }, [onPlaceSelect]);

  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      console.warn(
        "Google Maps API key not found. Please add VITE_GOOGLE_MAPS_API_KEY to your environment variables."
      );
      return;
    }

    if (!window.google || !window.google.maps || !window.google.maps.places) {
      const existingScript = document.querySelector(
        'script[src*="maps.googleapis.com"]'
      );
      if (existingScript) {
        existingScript.addEventListener('load', () => setIsLoaded(true));
        if (window.google?.maps?.places) {
          setIsLoaded(true);
        }
        return;
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setTimeout(() => setIsLoaded(true), 100);
      };
      script.onerror = () => {
        console.error("Failed to load Google Maps API.");
      };
      document.head.appendChild(script);
    } else {
      setIsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (
      isLoaded &&
      inputRef.current &&
      !autocompleteRef.current &&
      window.google?.maps?.places
    ) {
      try {
        // Use the classic Autocomplete class, not PlaceAutocompleteElement
        autocompleteRef.current = new window.google.maps.places.Autocomplete(
          inputRef.current,
          {
            types: ["establishment", "geocode"],
            componentRestrictions: { country: "ng" },
            fields: [
              "place_id",
              "formatted_address",
              "geometry",
              "address_components",
            ],
          }
        );

        autocompleteRef.current.addListener("place_changed", () => {
          const place = autocompleteRef.current.getPlace();

          if (place.geometry?.location) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();

            onPlaceSelectRef.current?.({
              address: place.formatted_address,
              latitude: lat,
              longitude: lng,
              placeId: place.place_id,
              addressComponents: place.address_components,
            });
          }
        });
      } catch (error) {
        console.error("Error initializing Google Places Autocomplete:", error);
      }
    }

    // Cleanup. Google's classic Autocomplete has no destroy() method and
    // appends a .pac-container dropdown directly to <body>, outside this
    // component's own DOM subtree. Left behind, it's an invisible,
    // absolutely-positioned element that can end up blocking clicks
    // elsewhere on the page (e.g. every time a modal using this field is
    // opened and closed, since destroyOnClose unmounts and remounts it).
    return () => {
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(
          autocompleteRef.current,
        );
        autocompleteRef.current = null;
      }
      document
        .querySelectorAll(".pac-container")
        .forEach((el) => el.remove());
    };
  }, [isLoaded]);

  const handleInputChange = (e) => {
    onChange(e.target.value);
  };

  // If Google Maps API is not available, show regular input
  if (!import.meta.env.VITE_GOOGLE_MAPS_API_KEY) {
    return (
      <div>
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${className}`}
        />
        <p className="text-xs text-yellow-600 mt-1">
          Google Places API not configured. Please add VITE_GOOGLE_MAPS_API_KEY
          to enable autocomplete.
        </p>
      </div>
    );
  }

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={handleInputChange}
      placeholder={placeholder}
      disabled={disabled}
      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${className}`}
    />
  );
};

export default GooglePlacesAutocomplete;
